import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Template from "../../components/Template";
import TabNavigation from "../../components/TabNavigation";
import api from "../../services/api";
import config from "../../config";
import { maskCurrency } from "../../utils/masks";
import ConfirmationModal from "../../components/ConfirmationModal";
import SuccessModal from "../../components/SuccessModal";
import DetalhesPagamentoForm from "../../components/DetalhesPagamentoForm";
import BoletosParceladosManager from "../../components/BoletosParceladosManager";
import { formatDateWithoutHour } from "../../utils/date";
import { parseCurrencyToFloat } from "../../utils/utils";
import usePermission from "../../hooks/usePermission";
import pedidoCompraService from '../../services/pedidoCompraService';
import * as XLSX from 'xlsx';

let tempItemId = 90000;

const PedidoCompraForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditMode = Boolean(id);
    const podeDeletar = usePermission("EXCLUIR_PEDIDO_COMPRA");
    const podeEditar = usePermission("EDITAR_PEDIDO_COMPRA");
    const podeCriar = usePermission("CRIAR_PEDIDO_COMPRA");
    const podeEditarQualquerStatus = usePermission("EDITAR_PEDIDO_COMPRA_QUALQUER_STATUS");

    const STATUS_NAO_EDITAVEIS = ['Aprovado', 'Recebido', 'Finalizado/Pago'];

    const [fornecedores, setFornecedores] = useState([]);
    const [obras, setObras] = useState([]);
    const [itensRemovidos, setItensRemovidos] = useState([]);
    const [tiposDocumentos, setTiposDocumentos] = useState([]);
    const [tiposImposto, setTiposImposto] = useState([]);
    
    // Estados para nota fiscal
    const [notasFiscais, setNotasFiscais] = useState([]);
    const [searchNotaFiscal, setSearchNotaFiscal] = useState("");
    const [showNotasFiscaisDropdown, setShowNotasFiscaisDropdown] = useState(false);
    const [notaFiscalSelecionada, setNotaFiscalSelecionada] = useState(null);
    
    // Estado para controlar aba ativa
    const [abaAtiva, setAbaAtiva] = useState("geral");
    
    // Estados para anexos
    const [anexoFile, setAnexoFile] = useState(null);
    const [anexosSelecionados, setAnexosSelecionados] = useState([]);
    const [anexosRemovidos, setAnexosRemovidos] = useState([]);
    const [tipoDocumentoAnexo, setTipoDocumentoAnexo] = useState("");

    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [showConfirmDeleteRegister, setShowConfirmDeleteRegister] = useState(false);
    const [showCancelarDialog, setShowCancelarDialog] = useState(false);
    const [motivoCancelamento, setMotivoCancelamento] = useState("");
    const [cancelando, setCancelando] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [numeroValidacao, setNumeroValidacao] = useState({ valido: true, mensagem: "" });
    const [validandoNumero, setValidandoNumero] = useState(false);
    const [pedidoOriginal, setPedidoOriginal] = useState(null);

    // Status que não permitem edição dos campos principais (boletos/anexos continuam liberados)
    const statusOriginalBloqueado = isEditMode && pedidoOriginal &&
        STATUS_NAO_EDITAVEIS.includes(
            pedidoOriginal.statusRelation?.nome ||
            (typeof pedidoOriginal.status === 'object' ? pedidoOriginal.status?.nome : null)
        );
    // Override: usuário com a permissão especial pode editar mesmo em status bloqueado
    const statusEhNaoEditavel = statusOriginalBloqueado && !podeEditarQualquerStatus;
    // Para mostrar banner de aviso quando estiver editando em status bloqueado via override
    const editandoEmStatusBloqueado = statusOriginalBloqueado && podeEditarQualquerStatus;
    
    // Referência para o timeout do debounce da validação de número
    const numeroValidacaoTimeoutRef = useRef(null);

    const [formData, setFormData] = useState({
        numero: "",
        dataEmissao: new Date().toISOString().split('T')[0],
        fornecedorId: "",
        obraId: "",
        escopo: "",
        formaPagamento: "",
        prazoEntrega: "",
        condicoesPagamento: "",
        observacoes: "",
        status: "RASCUNHO",
        itens: [],
        valorDesconto: 0,
        valorFrete: 0,
        // Campos de detalhes de pagamento
        isPagamentoParcelado: false,
        pixChave: "",
        pixTipoChave: "",
        pixVencimento: "",
        parcelamentoParcelas: "",
        parcelamentoIntervalo: "",
        parcelamentoDataPrimeira: "",
        parcelamentoObservacao: "",
        bancoNome: "",
        bancoCodigo: "",
        bancoAgencia: "",
        bancoConta: "",
        bancoTipoConta: "",
        bancoFavorecido: "",
        bancoCnpjCpfFavorecido: "",
        transferenciaVencimento: "",
        boletoVencimento: "",
        boletoNumero: "",
        boletoCodigoBarras: "",
        cartaoTipo: "",
        cartaoObservacao: "",
        pagamentoObservacoes: "",
        // Boletos parcelados
        boletosParcelados: [],
        // Retenções de impostos
        retencoes: []
    });

    const [novoItem, setNovoItem] = useState({
        descricao: "",
        quantidade: "",
        unidade: "UN",
        precoUnitario: "",
        valorTotal: 0
    });

    useEffect(() => {
        // Verificar permissões
        if (isEditMode && !podeEditar) {
            alert("Você não tem permissão para editar pedidos de compra");
            navigate("/pedidos-compra");
            return;
        }
        
        if (!isEditMode && !podeCriar) {
            alert("Você não tem permissão para criar pedidos de compra");
            navigate("/pedidos-compra");
            return;
        }

        carregarDados();
        if (isEditMode) {
            carregarPedido();
        }
        // Não gera número automático mais - usuário vai digitar
    }, [id]);

    // Buscar notas fiscais com debounce
    useEffect(() => {
        if (searchNotaFiscal.length >= 3) {
            const timeoutId = setTimeout(() => {
                buscarNotasFiscais();
            }, 500);
            return () => clearTimeout(timeoutId);
        } else {
            setNotasFiscais([]);
        }
    }, [searchNotaFiscal, formData.fornecedorId]);

    const buscarNotasFiscais = async () => {
        try {
            const params = new URLSearchParams();
            if (searchNotaFiscal) {
                params.append('search', searchNotaFiscal);
            }
            if (formData.fornecedorId) {
                params.append('fornecedorId', formData.fornecedorId);
            }
            
            const response = await api.get(`/notasfiscais/buscar-vinculo?${params.toString()}`);
            setNotasFiscais(response.data);
            setShowNotasFiscaisDropdown(true);
        } catch (error) {
            console.error('Erro ao buscar notas fiscais:', error);
            setNotasFiscais([]);
        }
    };

    const selecionarNotaFiscal = (nota) => {
        setNotaFiscalSelecionada(nota);
        setSearchNotaFiscal(`NF ${nota.numero}${nota.serie ? `/${nota.serie}` : ''} - ${nota.fornecedor.nomeFornecedor}`);
        setShowNotasFiscaisDropdown(false);
        setFormData(prev => ({ ...prev, notaFiscalId: nota.id }));
    };

    const removerNotaFiscal = () => {
        setNotaFiscalSelecionada(null);
        setSearchNotaFiscal("");
        setFormData(prev => ({ ...prev, notaFiscalId: null }));
    };

    const carregarDados = async () => {
        try {
            const [fornecedoresRes, obrasRes] = await Promise.all([
                api.get("/fornecedores/todos"),
                api.get("/obras/listar/todas")
            ]);
            setFornecedores(fornecedoresRes.data);
            setObras(obrasRes.data);

            // Carregar tipos de documentos (opcional - para anexos)
            try {
                const tiposRes = await api.get("/tiposdocumentos");
                setTiposDocumentos(tiposRes.data);
            } catch (error) {
                console.warn("Tipos de documentos não disponíveis:", error.message);
                // Não bloqueia o formulário se não houver tipos de documentos
                setTiposDocumentos([]);
            }

            // Carregar tipos de imposto (para retenções)
            try {
                const tiposImpRes = await api.get("/tiposimposto");
                setTiposImposto(Array.isArray(tiposImpRes.data) ? tiposImpRes.data : []);
            } catch (error) {
                console.warn("Tipos de imposto não disponíveis:", error.message);
                setTiposImposto([]);
            }
        } catch (error) {
            console.error("Erro ao carregar dados:", error);
        }
    };

    const gerarNumero = async () => {
        try {
            const response = await pedidoCompraService.gerarNumero();
            setFormData(prev => ({ ...prev, numero: response.numero }));
        } catch (error) {
            console.error("Erro ao gerar número:", error);
            // Deixar vazio - o número será gerado pelo backend ao salvar
            setFormData(prev => ({ ...prev, numero: "" }));
        }
    };

    // Validar se o número da OC já existe
    const validarNumeroUnico = async (numero) => {
        if (!numero || numero.trim() === "") {
            setNumeroValidacao({ valido: true, mensagem: "" });
            return true;
        }

        // Se estiver editando e o número não mudou, não precisa validar
        if (isEditMode && pedidoOriginal && numero === pedidoOriginal.numeroOC) {
            setNumeroValidacao({ valido: true, mensagem: "" });
            return true;
        }

        setValidandoNumero(true);
        try {
            const response = await pedidoCompraService.validarNumeroUnico(numero);
            const jaExiste = response.existe;
            
            if (jaExiste) {
                setNumeroValidacao({ 
                    valido: false, 
                    mensagem: "Este número já está em uso por outro pedido" 
                });
                return false;
            } else {
                setNumeroValidacao({ valido: true, mensagem: "" });
                return true;
            }
        } catch (error) {
            console.error("Erro ao validar número:", error);
            // Em caso de erro, permite continuar mas alerta
            setNumeroValidacao({ 
                valido: true, 
                mensagem: "Não foi possível validar o número" 
            });
            return true;
        } finally {
            setValidandoNumero(false);
        }
    };

    // Handler para mudança no número com debounce
    const handleNumeroChange = (e) => {
        const novoNumero = e.target.value;
        setFormData(prev => ({ ...prev, numero: novoNumero }));
        
        // Debounce para validação
        if (numeroValidacaoTimeoutRef.current) {
            clearTimeout(numeroValidacaoTimeoutRef.current);
        }
        
        numeroValidacaoTimeoutRef.current = setTimeout(() => {
            validarNumeroUnico(novoNumero);
        }, 800);
    };

    const carregarPedido = async () => {
        try {
            const pedido = await pedidoCompraService.buscarPorId(id);
            
            // Salvar pedido original para comparações
            setPedidoOriginal(pedido);
            
            // Separar anexos normais dos boletos parcelados
            let anexosNormais = [];
            let boletosParcelados = [];
            
            if (pedido.anexos && pedido.anexos.length > 0) {
                pedido.anexos.forEach(anexo => {
                    const anexoFormatado = {
                        id: anexo.id,
                        tipoDocumentoId: anexo.tipoId || anexo.tipo?.id,
                        tipoNome: anexo.tipo?.nome || anexo.tipo?.descricao || 'Documento',
                        nomeArquivo: anexo.arquivo?.split('/').pop() || '',
                        arquivo: anexo.arquivo,
                        urlArquivo: anexo.arquivo ? config.urlArquivo(anexo.arquivo) : null,
                        dataEnvio: anexo.dataEnvio,
                        isNew: false
                    };
                    
                    // Se é um boleto parcelado (tem numeroParcela)
                    if (anexo.numeroParcela && anexo.dataVencimento) {
                        boletosParcelados.push({
                            numeroParcela: anexo.numeroParcela,
                            dataVencimento: anexo.dataVencimento.split('T')[0],
                            nomeArquivo: anexoFormatado.nomeArquivo,
                            urlArquivo: anexoFormatado.urlArquivo,
                            id: anexo.id,
                            isNew: false
                        });
                    } else {
                        // Anexo normal
                        anexosNormais.push(anexoFormatado);
                    }
                });
                
                setAnexosSelecionados(anexosNormais);
            }
            
            setFormData({
                numero: pedido.numero || "",
                dataEmissao: pedido.dataEmissao ? pedido.dataEmissao.split('T')[0] : "",
                fornecedorId: pedido.fornecedorId || "",
                obraId: pedido.obraId || "",
                escopo: pedido.escopo || "",
                formaPagamento: pedido.formaPagamento || "",
                prazoEntrega: pedido.prazoEntrega || "",
                condicoesPagamento: pedido.condicoesPagamento || "",
                observacoes: pedido.observacao || "", // Backend retorna 'observacao'
                status: pedido.status || "RASCUNHO",
                itens: pedido.itens || [],
                valorDesconto: pedido.valorDesconto || 0,
                valorFrete: pedido.valorFrete || 0,
                valorDescontoFormatted: pedido.valorDesconto ? maskCurrency(Math.round((pedido.valorDesconto || 0) * 100)) : '',
                valorFreteFormatted: pedido.valorFrete ? maskCurrency(Math.round((pedido.valorFrete || 0) * 100)) : '',
                // Campos de detalhes de pagamento
                isPagamentoParcelado: (pedido.parcelamentoParcelas && pedido.parcelamentoParcelas > 0) || false,
                pixChave: pedido.pixChave || "",
                pixTipoChave: pedido.pixTipoChave || "",
                pixVencimento: pedido.pixVencimento ? pedido.pixVencimento.split('T')[0] : "",
                parcelamentoParcelas: pedido.parcelamentoParcelas || "",
                parcelamentoIntervalo: pedido.parcelamentoIntervalo || "",
                parcelamentoDataPrimeira: pedido.parcelamentoDataPrimeira ? pedido.parcelamentoDataPrimeira.split('T')[0] : "",
                parcelamentoObservacao: pedido.parcelamentoObservacao || "",
                bancoNome: pedido.bancoNome || "",
                bancoCodigo: pedido.bancoCodigo || "",
                bancoAgencia: pedido.bancoAgencia || "",
                bancoConta: pedido.bancoConta || "",
                bancoTipoConta: pedido.bancoTipoConta || "",
                bancoFavorecido: pedido.bancoFavorecido || "",
                bancoCnpjCpfFavorecido: pedido.bancoCnpjCpfFavorecido || "",
                transferenciaVencimento: pedido.transferenciaVencimento ? pedido.transferenciaVencimento.split('T')[0] : "",
                boletoVencimento: pedido.boletoVencimento ? pedido.boletoVencimento.split('T')[0] : "",
                boletoNumero: pedido.boletoNumero || "",
                boletoCodigoBarras: pedido.boletoCodigoBarras || "",
                cartaoTipo: pedido.cartaoTipo || "",
                cartaoObservacao: pedido.cartaoObservacao || "",
                pagamentoObservacoes: pedido.pagamentoObservacoes || "",
                boletosParcelados: boletosParcelados,
                notaFiscalId: pedido.notaFiscalId || null,
                retencoes: (pedido.retencoes || []).map(r => ({
                    id: r.id,
                    tipoImpostoId: r.tipoImpostoId,
                    tipoImpostoNome: r.tipoImposto?.nome || '',
                    aliquota: r.aliquota ?? '',
                    baseCalculo: r.baseCalculo ?? '',
                    valor: r.valor,
                    valorFormatted: r.valor != null ? maskCurrency(Math.round((r.valor || 0) * 100)) : '',
                    observacao: r.observacao || ''
                }))
            });
            
            // Carregar nota fiscal se houver
            if (pedido.notaFiscal) {
                setNotaFiscalSelecionada(pedido.notaFiscal);
                setSearchNotaFiscal(`NF ${pedido.notaFiscal.numero}${pedido.notaFiscal.serie ? `/${pedido.notaFiscal.serie}` : ''} - ${pedido.notaFiscal.fornecedor.nomeFornecedor}`);
            }
        } catch (error) {
            console.error("Erro ao carregar pedido:", error);
            alert("Erro ao carregar dados do pedido");
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleBoletosChange = (boletos) => {
        setFormData(prev => ({ ...prev, boletosParcelados: boletos }));
    };

    const handleNovoItemChange = (e) => {
        const { name, value } = e.target;
        let newValue = value;

        if (name === "precoUnitario") {
            newValue = maskCurrency(value);
        }

        setNovoItem(prev => {
            const updated = { ...prev, [name]: newValue };

            if (name === "quantidade" || name === "precoUnitario") {
                const quantidade = parseFloat(name === "quantidade" ? value : updated.quantidade) || 0;
                const preco = parseCurrencyToFloat(name === "precoUnitario" ? newValue : updated.precoUnitario) || 0;
                updated.valorTotal = quantidade * preco;
            }

            return updated;
        });
    };

    const adicionarItem = () => {
        if (!novoItem.descricao) {
            alert("Informe a descrição do item");
            return;
        }
        if (!novoItem.quantidade || parseFloat(novoItem.quantidade) <= 0) {
            alert("Informe uma quantidade válida");
            return;
        }
        if (!novoItem.precoUnitario) {
            alert("Informe o preço unitário");
            return;
        }

        const item = {
            id: tempItemId++,
            descricao: novoItem.descricao,
            quantidade: parseFloat(novoItem.quantidade),
            unidade: novoItem.unidade,
            precoUnitario: parseCurrencyToFloat(novoItem.precoUnitario),
            valorTotal: novoItem.valorTotal,
            isNew: true
        };

        setFormData(prev => ({
            ...prev,
            itens: [...prev.itens, item]
        }));

        setNovoItem({
            descricao: "",
            quantidade: "",
            unidade: "UN",
            precoUnitario: "",
            valorTotal: 0
        });
    };

    // ===== Handlers de Retenções de Impostos =====
    const adicionarRetencao = () => {
        setFormData(prev => ({
            ...prev,
            retencoes: [
                ...prev.retencoes,
                {
                    tipoImpostoId: "",
                    tipoImpostoNome: "",
                    aliquota: "",
                    baseCalculo: "",
                    valor: 0,
                    valorFormatted: "",
                    observacao: ""
                }
            ]
        }));
    };

    const removerRetencao = (index) => {
        setFormData(prev => ({
            ...prev,
            retencoes: prev.retencoes.filter((_, i) => i !== index)
        }));
    };

    const atualizarRetencao = (index, campo, valor) => {
        setFormData(prev => {
            const novas = [...prev.retencoes];
            const atual = { ...novas[index] };
            if (campo === 'valorFormatted') {
                atual.valorFormatted = maskCurrency(valor);
                atual.valor = parseCurrencyToFloat(atual.valorFormatted);
            } else if (campo === 'tipoImpostoId') {
                atual.tipoImpostoId = valor;
                const tipo = tiposImposto.find(t => String(t.id) === String(valor));
                atual.tipoImpostoNome = tipo ? tipo.nome : '';
            } else {
                atual[campo] = valor;
            }
            novas[index] = atual;
            return { ...prev, retencoes: novas };
        });
    };

    // Totais de retenções
    const calcularTotalRetencoes = () => {
        return formData.retencoes.reduce((acc, r) => acc + (parseFloat(r.valor) || 0), 0);
    };

    // Cancelar pedido (qualquer status) — requer motivo obrigatório
    const confirmarCancelamento = async () => {
        if (!motivoCancelamento || motivoCancelamento.trim().length === 0) {
            alert("Informe o motivo do cancelamento.");
            return;
        }
        try {
            setCancelando(true);
            await api.patch(`/pedidos-compra/${id}/cancelar`, { motivo: motivoCancelamento.trim() });
            setShowCancelarDialog(false);
            setMotivoCancelamento("");
            setSuccessMessage("Pedido cancelado com sucesso!");
            setShowSuccessDialog(true);
        } catch (error) {
            console.error("Erro ao cancelar pedido:", error);
            alert(error.response?.data?.error || "Erro ao cancelar pedido");
        } finally {
            setCancelando(false);
        }
    };

    const handleImportarExcel = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
                
                // Ler todas as linhas
                const jsonData = XLSX.utils.sheet_to_json(firstSheet, { 
                    header: 1,
                    raw: false,
                    defval: ''
                });

                // Procurar linha do cabeçalho
                let headerIndex = -1;
                let headers = [];
                
                for (let i = 0; i < jsonData.length; i++) {
                    const row = jsonData[i];
                    const rowStr = row.join('|').toUpperCase();
                    
                    if (rowStr.includes('QTDE') && rowStr.includes('DESCRIÇÃO') && rowStr.includes('UNIT')) {
                        headerIndex = i;
                        headers = row.map(h => String(h || '').trim());
                        break;
                    }
                }

                if (headerIndex === -1) {
                    alert('❌ Não foi possível encontrar o cabeçalho na planilha.\n\nUse o template padrão ou certifique-se que a planilha contém as colunas:\nQTDE | UND | DESCRIÇÃO DO PRODUTO | UNIT (R$)');
                    return;
                }

                // Encontrar índices das colunas
                const colQtde = headers.findIndex(h => h.toUpperCase().includes('QTDE'));
                const colUnd = headers.findIndex(h => h.toUpperCase().includes('UND'));
                const colDescricao = headers.findIndex(h => h.toUpperCase().includes('DESCRIÇÃO') || h.toUpperCase().includes('DESCRICAO'));
                const colUnit = headers.findIndex(h => {
                    const upper = h.toUpperCase();
                    return (upper.includes('UNIT') || (upper.includes('PREÇO') && !upper.includes('TOTAL'))) && 
                           !upper.includes('TOTAL');
                });

                if (colQtde === -1 || colDescricao === -1 || colUnit === -1) {
                    alert('❌ Não foi possível identificar todas as colunas necessárias.\n\nColunas obrigatórias: QTDE, DESCRIÇÃO, UNIT (R$)');
                    return;
                }

                const itensImportados = [];
                const erros = [];

                // Processar linhas de dados
                for (let i = headerIndex + 1; i < jsonData.length; i++) {
                    const row = jsonData[i];
                    
                    // Pular linhas vazias
                    if (!row || row.every(cell => !cell || cell === '')) {
                        continue;
                    }

                    const quantidade = row[colQtde];
                    const unidade = colUnd >= 0 ? row[colUnd] : 'UN';
                    const descricao = row[colDescricao];
                    const precoUnitarioRaw = row[colUnit];

                    // Validar descrição
                    if (!descricao || String(descricao).trim() === '' || String(descricao).toUpperCase().includes('EXEMPLO')) {
                        continue; // Pular linhas sem descrição ou linhas de exemplo
                    }

                    // Validar quantidade
                    const qtd = parseFloat(String(quantidade || '0').replace(',', '.'));
                    if (isNaN(qtd) || qtd <= 0) {
                        erros.push(`Linha ${i + 1}: Quantidade inválida (${quantidade})`);
                        continue;
                    }

                    // Validar e converter preço
                    let precoUnitario = 0;
                    if (precoUnitarioRaw) {
                        // Se for número, usar diretamente
                        if (typeof precoUnitarioRaw === 'number') {
                            precoUnitario = precoUnitarioRaw;
                        } else {
                            // Se for string, processar
                            let priceStr = String(precoUnitarioRaw)
                                .replace(/[^\d,.-]/g, '') // Remove tudo exceto números, vírgula, ponto e hífen
                                .trim();
                            
                            // Detectar formato: se tem vírgula E ponto, determinar qual é decimal
                            if (priceStr.includes(',') && priceStr.includes('.')) {
                                const lastComma = priceStr.lastIndexOf(',');
                                const lastDot = priceStr.lastIndexOf('.');
                                
                                if (lastComma > lastDot) {
                                    // Vírgula é decimal (formato BR: 1.234,56)
                                    priceStr = priceStr.replace(/\./g, '').replace(',', '.');
                                } else {
                                    // Ponto é decimal (formato US: 1,234.56)
                                    priceStr = priceStr.replace(/,/g, '');
                                }
                            } else if (priceStr.includes(',')) {
                                // Só tem vírgula - pode ser decimal ou milhar
                                // Se tem dígitos depois da vírgula <= 2, é decimal
                                const parts = priceStr.split(',');
                                if (parts[1] && parts[1].length <= 2) {
                                    priceStr = priceStr.replace(',', '.');
                                } else {
                                    priceStr = priceStr.replace(/,/g, '');
                                }
                            }
                            // Se só tem ponto, assumir que é decimal (formato US ou valores pequenos)
                            
                            precoUnitario = parseFloat(priceStr);
                        }
                    }

                    if (isNaN(precoUnitario) || precoUnitario <= 0) {
                        erros.push(`Linha ${i + 1}: Preço unitário inválido (${precoUnitarioRaw})`);
                        continue;
                    }

                    const valorTotal = qtd * precoUnitario;

                    // Normalizar unidade para os valores aceitos pelo sistema
                    let unidadeNormalizada = 'UN';
                    if (unidade) {
                        const unidadeUpper = String(unidade).trim().toUpperCase();
                        // Mapear variações para as unidades padrão
                        const mapaUnidades = {
                            'UN': 'UN',
                            'UND': 'UN',
                            'UNID': 'UN',
                            'UNIDADE': 'UN',
                            'KG': 'KG',
                            'KILO': 'KG',
                            'QUILOGRAMA': 'KG',
                            'MT': 'MT',
                            'M': 'MT',
                            'METRO': 'MT',
                            'CX': 'CX',
                            'CAIXA': 'CX',
                            'PC': 'PC',
                            'PEÇA': 'PC',
                            'PECA': 'PC',
                            'LT': 'LT',
                            'LITRO': 'LT',
                            'L': 'LT'
                        };
                        unidadeNormalizada = mapaUnidades[unidadeUpper] || 'UN';
                    }

                    itensImportados.push({
                        id: tempItemId++,
                        descricao: String(descricao).trim(),
                        quantidade: qtd,
                        unidade: unidadeNormalizada,
                        precoUnitario: precoUnitario,
                        valorTotal: valorTotal,
                        isNew: true
                    });
                }

                if (erros.length > 0 && itensImportados.length > 0) {
                    alert(`⚠️ Alguns itens com erro:\n\n${erros.slice(0, 5).join('\n')}${erros.length > 5 ? '\n...' : ''}\n\n✅ ${itensImportados.length} ${itensImportados.length === 1 ? 'item importado' : 'itens importados'}.`);
                } else if (erros.length > 0) {
                    alert(`❌ Erros encontrados:\n\n${erros.slice(0, 10).join('\n')}\n\nNenhum item válido foi importado.`);
                }

                if (itensImportados.length > 0) {
                    setFormData(prev => ({
                        ...prev,
                        itens: [...prev.itens, ...itensImportados]
                    }));
                    
                    if (erros.length === 0) {
                        alert(`✅ ${itensImportados.length} ${itensImportados.length === 1 ? 'item importado' : 'itens importados'} com sucesso!`);
                    }
                } else {
                    alert('❌ Nenhum item válido foi encontrado na planilha.\n\nVerifique se há dados após o cabeçalho.');
                }

            } catch (error) {
                console.error('Erro ao importar Excel:', error);
                alert('❌ Erro ao processar o arquivo Excel.\n\n' + error.message);
            }
        };

        reader.readAsArrayBuffer(file);
        event.target.value = '';
    };

    const downloadTemplateExcel = () => {
        // Criar workbook
        const wb = XLSX.utils.book_new();
        
        // Dados do template
        const templateData = [
            ['ITEM', 'QTDE', 'UND', 'DESCRIÇÃO DO PRODUTO', 'UNIT (R$)', 'TOTAL (R$)'],
            [1, 10, 'UN', 'Exemplo: Cimento CP II 50KG', 35.50, 355.00],
            [2, 500, 'KG', 'Exemplo: Areia Média Lavada', 0.45, 225.00],
            [3, 15, 'MT', 'Exemplo: Cabo Flexível 2,5mm', 12.80, 192.00],
            [4, 20, 'PC', 'Exemplo: Tijolo Cerâmico 6 Furos', 0.85, 17.00],
            [5, 5, 'LT', 'Exemplo: Tinta Acrílica Branca 18L', 89.90, 449.50],
            ['', '', '', '', '', ''],
            ['', '', '', 'Instruções:', '', ''],
            ['', '', '', '1. Mantenha o cabeçalho na primeira linha', '', ''],
            ['', '', '', '2. Preencha QTDE, UND, DESCRIÇÃO e UNIT (R$)', '', ''],
            ['', '', '', '3. A coluna TOTAL é opcional (calculada automaticamente no sistema)', '', ''],
            ['', '', '', '4. Você pode adicionar quantas linhas precisar', '', ''],
            ['', '', '', '5. Salve e importe o arquivo', '', '']
        ];
        
        const ws = XLSX.utils.aoa_to_sheet(templateData);
        
        // Ajustar larguras das colunas
        ws['!cols'] = [
            { wch: 6 },  // ITEM
            { wch: 8 },  // QTDE
            { wch: 6 },  // UND
            { wch: 50 }, // DESCRIÇÃO
            { wch: 12 }, // UNIT
            { wch: 12 }  // TOTAL
        ];
        
        XLSX.utils.book_append_sheet(wb, ws, 'Itens');
        
        // Gerar arquivo e fazer download
        XLSX.writeFile(wb, 'template_itens_pedido_compra.xlsx');
    };

    const removerItem = (itemId) => {
        const item = formData.itens.find(i => i.id === itemId);
        if (item && !item.isNew) {
            setItensRemovidos(prev => [...prev, item.id]);
        }
        setFormData(prev => ({
            ...prev,
            itens: prev.itens.filter(i => i.id !== itemId)
        }));
    };

    const editarItem = (index) => {
        setFormData(prev => ({
            ...prev,
            itens: prev.itens.map((item, i) => 
                i === index ? { ...item, editando: true } : item
            )
        }));
    };

    const salvarEdicaoItem = (index) => {
        setFormData(prev => {
            const novosItens = [...prev.itens];
            const item = novosItens[index];
            item.valorTotal = item.quantidade * item.precoUnitario;
            item.editando = false;
            return { ...prev, itens: novosItens };
        });
    };

    const cancelarEdicaoItem = (index) => {
        // Recarregar o pedido original se estiver em modo de edição
        if (isEditMode) {
            carregarPedido();
        } else {
            // Se for item novo, apenas cancela o modo de edição
            setFormData(prev => ({
                ...prev,
                itens: prev.itens.map((item, i) => 
                    i === index ? { ...item, editando: false } : item
                )
            }));
        }
    };

    const atualizarItemEdicao = (index, campo, valor) => {
        setFormData(prev => {
            const novosItens = [...prev.itens];
            let valorProcessado = valor;

            // Processar valor baseado no campo
            if (campo === 'quantidade') {
                valorProcessado = parseFloat(valor) || 0;
            } else if (campo === 'precoUnitario') {
                valorProcessado = parseFloat(valor) || 0;
            }

            novosItens[index] = {
                ...novosItens[index],
                [campo]: valorProcessado
            };

            // Recalcular valor total se quantidade ou preço mudar
            if (campo === 'quantidade' || campo === 'precoUnitario') {
                novosItens[index].valorTotal = 
                    novosItens[index].quantidade * novosItens[index].precoUnitario;
            }

            return { ...prev, itens: novosItens };
        });
    };

    const calcularValorTotal = () => {
        return formData.itens.reduce((total, item) => total + (item.valorTotal || 0), 0);
    };

    const calcularSubtotal = () => {
        return formData.itens.reduce((total, item) => total + (item.valorTotal || 0), 0);
    };

    const calcularValorFinal = () => {
        const subtotal = calcularSubtotal();
        const desconto = parseFloat(formData.valorDesconto) || 0;
        const frete = parseFloat(formData.valorFrete) || 0;
        return subtotal - desconto + frete;
    };

    // Funções para gerenciar anexos
    const handleAnexoFileChange = (e) => {
        setAnexoFile(e.target.files[0]);
    };

    const handleAddAnexo = () => {
        if (!tipoDocumentoAnexo) {
            alert("Selecione um Tipo de Documento antes de enviar.");
            return;
        }
        if (!anexoFile) {
            alert("Selecione um arquivo antes de enviar.");
            return;
        }

        const tipoId = parseInt(tipoDocumentoAnexo, 10);
        const tipoEncontrado = tiposDocumentos.find((t) => t.id === tipoId);
        if (!tipoEncontrado) {
            alert("Tipo de documento não encontrado.");
            return;
        }

        const novoAnexo = {
            id: Date.now(),
            tipoDocumentoId: tipoId,
            tipoNome: tipoEncontrado.nome,
            nomeArquivo: anexoFile.name,
            arquivo: anexoFile,
            file: anexoFile,
            dataEnvio: new Date().toISOString(),
            isNew: true,
        };
        
        setAnexosSelecionados((prev) => [...prev, novoAnexo]);
        setAnexoFile(null);
        setTipoDocumentoAnexo("");
    };

    const handleRemoveAnexo = (anexoId) => {
        const item = anexosSelecionados.find((a) => a.id === anexoId);
        if (!item) return;

        if (item.isNew) {
            setAnexosSelecionados((prev) => prev.filter((a) => a.id !== anexoId));
        } else {
            setAnexosRemovidos((prev) => [...prev, item]);
            setAnexosSelecionados((prev) => prev.filter((a) => a.id !== anexoId));
        }
    };

    const validarFormulario = () => {
        if (!formData.numero || formData.numero.trim() === "") {
            alert("Informe o número do pedido");
            return false;
        }
        if (!formData.fornecedorId) {
            alert("Selecione um fornecedor");
            return false;
        }
        if (!formData.obraId) {
            alert("Selecione uma obra");
            return false;
        }
        if (formData.itens.length === 0) {
            alert("Adicione pelo menos um item ao pedido");
            return false;
        }
        // Validar retenções
        for (let i = 0; i < formData.retencoes.length; i++) {
            const r = formData.retencoes[i];
            if (!r.tipoImpostoId) {
                alert(`Selecione o tipo de imposto na retenção ${i + 1}`);
                return false;
            }
            if (!r.valor || parseFloat(r.valor) <= 0) {
                alert(`Informe o valor da retenção ${i + 1}`);
                return false;
            }
        }
        if (calcularTotalRetencoes() > calcularValorFinal()) {
            alert("A soma das retenções não pode exceder o valor final do pedido");
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        console.log("handleSubmit chamado");
        console.log("Validando formulário...");
        if (!validarFormulario()) {
            console.log("Validação falhou");
            return;
        }

        // Validar número único
        if (!formData.numero || formData.numero.trim() === "") {
            alert("Por favor, informe o número do pedido");
            return;
        }

        if (!numeroValidacao.valido) {
            alert("O número informado já está em uso. Por favor, utilize outro número.");
            return;
        }

        // Validar número novamente antes de salvar (segurança)
        const numeroValido = await validarNumeroUnico(formData.numero);
        if (!numeroValido) {
            alert("O número informado já está em uso. Por favor, utilize outro número.");
            return;
        }

        console.log("Validação OK, abrindo modal de confirmação");
        setShowConfirmDialog(true);
    };

    const confirmarSalvamento = async () => {
        console.log("Confirmando salvamento...");
        setShowConfirmDialog(false);

        try {
            const formDataToSend = new FormData();
            
            // Dados básicos
            // Sempre enviar o número (agora é obrigatório e digitado pelo usuário)
            formDataToSend.append('numeroOC', formData.numero);
            formDataToSend.append('dataEmissao', formData.dataEmissao);
            formDataToSend.append('fornecedorId', formData.fornecedorId);
            if (formData.obraId) formDataToSend.append('obraId', formData.obraId);
            if (formData.notaFiscalId) formDataToSend.append('notaFiscalId', formData.notaFiscalId); // Nota fiscal vinculada
            if (formData.escopo) formDataToSend.append('escopo', formData.escopo);
            if (formData.formaPagamento) formDataToSend.append('formaPagamento', formData.formaPagamento);
            if (formData.prazoEntrega) formDataToSend.append('prazoEntrega', formData.prazoEntrega);
            if (formData.condicoesPagamento) formDataToSend.append('condicoesPagamento', formData.condicoesPagamento);
            if (formData.observacoes) formDataToSend.append('observacao', formData.observacoes);
            formDataToSend.append('valorTotal', calcularSubtotal()); // Subtotal dos itens
            formDataToSend.append('valorDesconto', formData.valorDesconto || 0);
            formDataToSend.append('valorFrete', formData.valorFrete || 0);
            // valorFinal será calculado no backend: subtotal - desconto + frete
            
            // Dados de detalhes de pagamento
            formDataToSend.append('isPagamentoParcelado', formData.isPagamentoParcelado || false);
            if (formData.pixChave) formDataToSend.append('pixChave', formData.pixChave);
            if (formData.pixTipoChave) formDataToSend.append('pixTipoChave', formData.pixTipoChave);
            if (formData.pixVencimento) formDataToSend.append('pixVencimento', formData.pixVencimento);
            if (formData.parcelamentoParcelas) formDataToSend.append('parcelamentoParcelas', formData.parcelamentoParcelas);
            if (formData.parcelamentoIntervalo) formDataToSend.append('parcelamentoIntervalo', formData.parcelamentoIntervalo);
            if (formData.parcelamentoDataPrimeira) formDataToSend.append('parcelamentoDataPrimeira', formData.parcelamentoDataPrimeira);
            if (formData.parcelamentoObservacao) formDataToSend.append('parcelamentoObservacao', formData.parcelamentoObservacao);
            if (formData.bancoNome) formDataToSend.append('bancoNome', formData.bancoNome);
            if (formData.bancoCodigo) formDataToSend.append('bancoCodigo', formData.bancoCodigo);
            if (formData.bancoAgencia) formDataToSend.append('bancoAgencia', formData.bancoAgencia);
            if (formData.bancoConta) formDataToSend.append('bancoConta', formData.bancoConta);
            if (formData.bancoTipoConta) formDataToSend.append('bancoTipoConta', formData.bancoTipoConta);
            if (formData.bancoFavorecido) formDataToSend.append('bancoFavorecido', formData.bancoFavorecido);
            if (formData.bancoCnpjCpfFavorecido) formDataToSend.append('bancoCnpjCpfFavorecido', formData.bancoCnpjCpfFavorecido);
            if (formData.transferenciaVencimento) formDataToSend.append('transferenciaVencimento', formData.transferenciaVencimento);
            if (formData.boletoVencimento) formDataToSend.append('boletoVencimento', formData.boletoVencimento);
            if (formData.boletoNumero) formDataToSend.append('boletoNumero', formData.boletoNumero);
            if (formData.boletoCodigoBarras) formDataToSend.append('boletoCodigoBarras', formData.boletoCodigoBarras);
            if (formData.cartaoTipo) formDataToSend.append('cartaoTipo', formData.cartaoTipo);
            if (formData.cartaoObservacao) formDataToSend.append('cartaoObservacao', formData.cartaoObservacao);
            if (formData.pagamentoObservacoes) formDataToSend.append('pagamentoObservacoes', formData.pagamentoObservacoes);
            
            // Itens - garantir que está enviando o array correto
            console.log('Itens a enviar:', formData.itens);
            formDataToSend.append('itens', JSON.stringify(formData.itens.map(item => ({
                descricao: item.descricao,
                quantidade: item.quantidade,
                unidade: item.unidade,
                precoUnitario: item.precoUnitario,
                valorTotal: item.valorTotal
            }))));
            
            // Itens removidos (apenas no modo edição)
            if (isEditMode && itensRemovidos.length > 0) {
                formDataToSend.append('itensRemovidos', JSON.stringify(itensRemovidos));
            }

            // Retenções de impostos
            const retencoesPayload = (formData.retencoes || []).map(r => ({
                tipoImpostoId: parseInt(r.tipoImpostoId),
                aliquota: r.aliquota === '' || r.aliquota === null || r.aliquota === undefined ? null : parseFloat(r.aliquota),
                baseCalculo: r.baseCalculo === '' || r.baseCalculo === null || r.baseCalculo === undefined ? null : parseFloat(r.baseCalculo),
                valor: parseFloat(r.valor) || 0,
                observacao: r.observacao || null
            })).filter(r => !isNaN(r.tipoImpostoId));
            formDataToSend.append('retencoes', JSON.stringify(retencoesPayload));
            
            // Anexos
            const anexosMetadata = anexosSelecionados.map(anexo => ({
                tipo: anexo.tipoDocumentoId,
                dataEnvio: anexo.dataEnvio,
                isNew: anexo.isNew
            }));
            formDataToSend.append('anexos', JSON.stringify(anexosMetadata));
            
            // Arquivos dos anexos
            anexosSelecionados.forEach((anexo, index) => {
                if (anexo.isNew && anexo.file) {
                    formDataToSend.append(`anexosFiles[${index}]`, anexo.file);
                }
            });
            
            // Anexos removidos (apenas no modo edição)
            if (isEditMode && anexosRemovidos.length > 0) {
                formDataToSend.append('anexosRemovidos', JSON.stringify(anexosRemovidos));
            }

            // Boletos parcelados
            if (formData.formaPagamento === "BOLETO" && 
                formData.isPagamentoParcelado && 
                formData.boletosParcelados && 
                formData.boletosParcelados.length > 0) {
                
                const boletosMetadata = formData.boletosParcelados.map(boleto => ({
                    numeroParcela: boleto.numeroParcela,
                    dataVencimento: boleto.dataVencimento,
                    isNew: boleto.isNew !== undefined ? boleto.isNew : true
                }));
                formDataToSend.append('boletosParcelados', JSON.stringify(boletosMetadata));
                
                // Arquivos dos boletos
                formData.boletosParcelados.forEach((boleto, index) => {
                    if (boleto.arquivo) {
                        formDataToSend.append(`boletosFiles[${index}]`, boleto.arquivo);
                    }
                });
            }

            if (isEditMode) {
                await api.put(`/pedidos-compra/${id}`, formDataToSend, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                setSuccessMessage("Pedido atualizado com sucesso!");
            } else {
                await api.post('/pedidos-compra', formDataToSend, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                setSuccessMessage("Pedido cadastrado com sucesso!");
            }

            setShowSuccessDialog(true);
        } catch (error) {
            console.error("Erro ao salvar pedido:", error);
            alert(error.response?.data?.message || "Erro ao salvar pedido de compra");
        }
    };

    const handleEnviarParaOrcamento = async () => {
        if (!validarFormulario()) return;

        if (window.confirm("Deseja enviar este pedido para orçamento? Apenas usuários com permissão poderão visualizá-lo.")) {
            try {
                let pedidoId = id;

                if (!isEditMode) {
                    const pedidoData = {
                        ...formData,
                        observacao: formData.observacoes,
                        observacoes: undefined,
                        valorTotal: calcularValorTotal(),
                        itens: formData.itens.map(item => ({
                            descricao: item.descricao,
                            quantidade: item.quantidade,
                            unidade: item.unidade,
                            precoUnitario: item.precoUnitario,
                            valorTotal: item.valorTotal
                        }))
                    };
                    const novoPedido = await pedidoCompraService.criar(pedidoData);
                    pedidoId = novoPedido.id;
                }

                await pedidoCompraService.enviarParaOrcamento(pedidoId);
                console.log('✅ Pedido enviado para orçamento com sucesso! ID:', pedidoId);
                setSuccessMessage("Pedido enviado para orçamento com sucesso!");
                setShowSuccessDialog(true);
            } catch (error) {
                console.error("Erro ao enviar para orçamento:", error);
                alert(error.response?.data?.error || error.response?.data?.message || "Erro ao enviar pedido para orçamento");
            }
        }
    };

    const handleEnviarAprovacao = async () => {
        if (!validarFormulario()) return;

        if (window.confirm("Deseja enviar este pedido para aprovação?")) {
            try {
                let pedidoId = id;

                if (!isEditMode) {
                    const pedidoData = {
                        ...formData,
                        observacao: formData.observacoes, // Mapear observacoes para observacao
                        observacoes: undefined, // Remover observacoes
                        valorTotal: calcularValorTotal(),
                        itens: formData.itens.map(item => ({
                            descricao: item.descricao,
                            quantidade: item.quantidade,
                            unidade: item.unidade,
                            precoUnitario: item.precoUnitario,
                            valorTotal: item.valorTotal
                        }))
                    };
                    const novoPedido = await pedidoCompraService.criar(pedidoData);
                    pedidoId = novoPedido.id;
                }

                await pedidoCompraService.enviarParaAprovacao(pedidoId);
                console.log('✅ Pedido enviado para aprovação com sucesso! ID:', pedidoId);
                setSuccessMessage("Pedido enviado para aprovação com sucesso!");
                setShowSuccessDialog(true);
            } catch (error) {
                console.error("Erro ao enviar para aprovação:", error);
                alert(error.response?.data?.error || error.response?.data?.message || "Erro ao enviar pedido para aprovação");
            }
        }
    };

    const handleDeletar = () => {
        setShowConfirmDeleteRegister(true);
    };

    const confirmarDelecao = async () => {
        setShowConfirmDeleteRegister(false);

        try {
            await api.delete(`/pedidos-compra/${id}`);
            setSuccessMessage("Pedido excluído com sucesso!");
            setShowSuccessDialog(true);
        } catch (error) {
            console.error("Erro ao deletar pedido:", error);
            alert("Erro ao excluir pedido");
        }
    };

    const handleSuccessClose = () => {
        setShowSuccessDialog(false);
        navigate("/pedidos-compra");
    };

    return (
        <Template>
            <div className="cadastrar-ata-page" style={{ padding: "20px" }}>
                <div className="header-section" style={{ marginBottom: "30px" }}>
                    <h1 className="form-titulo" style={{ fontSize: "28px", fontWeight: "700", color: "#1F2937", marginBottom: "8px" }}>
                        {statusEhNaoEditavel
                            ? "Gerenciar Boletos / Anexos"
                            : isEditMode ? "Editar Pedido de Compra" : "Novo Pedido de Compra"}
                    </h1>
                    <p style={{ fontSize: "14px", color: "#6B7280" }}>
                        {formData.numero && `Pedido: ${formData.numero}`}
                    </p>
                </div>

                {statusEhNaoEditavel && (
                    <div style={{
                        backgroundColor: "#FEF9C3",
                        border: "1px solid #FDE047",
                        borderRadius: "8px",
                        padding: "14px 18px",
                        marginBottom: "24px",
                        fontSize: "14px",
                        color: "#854D0E"
                    }}>
                        ⚠️ Este pedido está com status <strong>{pedidoOriginal?.statusRelation?.nome || pedidoOriginal?.status?.nome}</strong>. Apenas boletos parcelados e anexos podem ser gerenciados.
                    </div>
                )}

                {editandoEmStatusBloqueado && (
                    <div style={{
                        backgroundColor: "#FFEDD5",
                        border: "1px solid #FB923C",
                        borderRadius: "8px",
                        padding: "14px 18px",
                        marginBottom: "24px",
                        fontSize: "14px",
                        color: "#9A3412",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px"
                    }}>
                        <span style={{ fontSize: "20px" }}>⚠️</span>
                        <div>
                            <strong>Edição em status restrito</strong> — este pedido está em <strong>{pedidoOriginal?.statusRelation?.nome || pedidoOriginal?.status?.nome}</strong>.
                            Você tem autorização para alterá-lo, mas a alteração será registrada no histórico de auditoria.
                        </div>
                    </div>
                )}

                {isEditMode && podeEditarQualquerStatus && pedidoOriginal &&
                    (pedidoOriginal.statusRelation?.nome || pedidoOriginal.status?.nome) !== 'Cancelada' && (
                    <div style={{ marginBottom: "16px" }}>
                        <button
                            type="button"
                            onClick={() => setShowCancelarDialog(true)}
                            style={{
                                backgroundColor: "#DC2626",
                                color: "white",
                                border: "none",
                                borderRadius: "6px",
                                padding: "10px 18px",
                                cursor: "pointer",
                                fontWeight: "600",
                                fontSize: "14px"
                            }}
                        >
                            🚫 Cancelar Pedido
                        </button>
                    </div>
                )}

                {/* Navegação por Abas */}
                <TabNavigation
                    abaAtiva={abaAtiva}
                    setAbaAtiva={setAbaAtiva}
                    contadores={{
                        itens: formData.itens.length,
                        anexos: anexosSelecionados.length,
                        retencoes: formData.retencoes.length
                    }}
                />

                {/* Container do Conteúdo das Abas */}
                <div className="form-tab-content" style={{
                    backgroundColor: "white",
                    borderRadius: "0 0 12px 12px",
                    padding: "32px",
                    marginBottom: "24px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    minHeight: "600px"
                }}>

                {/* ======================== ABA: DADOS GERAIS ======================== */}
                {abaAtiva === "geral" && (
                    <div>
                        <fieldset disabled={statusEhNaoEditavel} style={{ border: 'none', padding: 0, margin: 0 }}>
                        <h2 style={{ 
                            fontSize: "20px", 
                            fontWeight: "600", 
                            marginBottom: "24px", 
                            color: "#1F2937",
                            borderBottom: "2px solid #E5E7EB",
                            paddingBottom: "12px"
                        }}>
                            📋 Dados Gerais do Pedido
                        </h2>

                {/* Dados Principais */}
                <div style={{
                    backgroundColor: "transparent"
                }}>
                    <div className="form-fields-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "16px" }}>
                        <div>
                            <label style={{ display: "block", fontSize: "14px", fontWeight: "500", marginBottom: "6px", color: "#374151" }}>
                                Número do Pedido {!isEditMode && "*"}
                                {isEditMode && (
                                    <span title="O número da OC só pode ser alterado por usuários com a permissão RENUMERAR_PEDIDO_COMPRA, na tela de detalhes" style={{ marginLeft: "6px" }}>
                                        🔒
                                    </span>
                                )}
                            </label>
                            <input
                                type="text"
                                name="numero"
                                value={formData.numero || ""}
                                onChange={handleNumeroChange}
                                placeholder="Ex: OC-2025-0001"
                                readOnly={isEditMode}
                                disabled={isEditMode}
                                style={{
                                    width: "100%",
                                    padding: "10px 12px",
                                    border: numeroValidacao.valido ? "1px solid #D1D5DB" : "1px solid #EF4444",
                                    borderRadius: "8px",
                                    fontSize: "14px",
                                    backgroundColor: isEditMode ? "#F3F4F6" : "#FFFFFF",
                                    color: isEditMode ? "#6B7280" : "#111827",
                                    cursor: isEditMode ? "not-allowed" : "text"
                                }}
                            />
                            {isEditMode ? (
                                <span style={{ fontSize: "12px", color: "#6B7280", marginTop: "4px", display: "block" }}>
                                    🔒 Número fixo após criação. Para alterar, abra os detalhes do pedido.
                                </span>
                            ) : (
                                <>
                                    {validandoNumero && (
                                        <span style={{ fontSize: "12px", color: "#6B7280", marginTop: "4px", display: "block" }}>
                                            🔄 Validando número...
                                        </span>
                                    )}
                                    {!validandoNumero && !numeroValidacao.valido && (
                                        <span style={{ fontSize: "12px", color: "#EF4444", marginTop: "4px", display: "block" }}>
                                            ❌ {numeroValidacao.mensagem}
                                        </span>
                                    )}
                                    {!validandoNumero && numeroValidacao.valido && formData.numero && (
                                        <span style={{ fontSize: "12px", color: "#10B981", marginTop: "4px", display: "block" }}>
                                            ✓ Número disponível
                                        </span>
                                    )}
                                </>
                            )}
                        </div>

                        <div>
                            <label style={{ display: "block", fontSize: "14px", fontWeight: "500", marginBottom: "6px", color: "#374151" }}>
                                Data de Emissão *
                            </label>
                            <input
                                type="date"
                                name="dataEmissao"
                                value={formData.dataEmissao}
                                onChange={handleInputChange}
                                style={{
                                    width: "100%",
                                    padding: "10px 12px",
                                    border: "1px solid #D1D5DB",
                                    borderRadius: "8px",
                                    fontSize: "14px"
                                }}
                            />
                        </div>

                        <div>
                            <label style={{ display: "block", fontSize: "14px", fontWeight: "500", marginBottom: "6px", color: "#374151" }}>
                                Fornecedor *
                            </label>
                            <select
                                name="fornecedorId"
                                value={formData.fornecedorId}
                                onChange={handleInputChange}
                                style={{
                                    width: "100%",
                                    padding: "10px 12px",
                                    border: "1px solid #D1D5DB",
                                    borderRadius: "8px",
                                    fontSize: "14px"
                                }}
                            >
                                <option value="">Selecione um fornecedor</option>
                                {fornecedores.map(f => (
                                    <option key={f.id} value={f.id}>
                                        {f.nome || f.nomeFornecedor}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label style={{ display: "block", fontSize: "14px", fontWeight: "500", marginBottom: "6px", color: "#374151" }}>
                                Nota Fiscal (Opcional)
                            </label>
                            <div style={{ position: "relative" }}>
                                <input
                                    type="text"
                                    value={searchNotaFiscal}
                                    onChange={(e) => setSearchNotaFiscal(e.target.value)}
                                    placeholder="Digite o número da nota fiscal (mín. 3 caracteres)"
                                    disabled={!formData.fornecedorId}
                                    style={{
                                        width: "100%",
                                        padding: "10px 12px",
                                        paddingRight: notaFiscalSelecionada ? "40px" : "12px",
                                        border: "1px solid #D1D5DB",
                                        borderRadius: "8px",
                                        fontSize: "14px",
                                        backgroundColor: !formData.fornecedorId ? "#F3F4F6" : "white"
                                    }}
                                />
                                {notaFiscalSelecionada && (
                                    <button
                                        type="button"
                                        onClick={removerNotaFiscal}
                                        title="Remover nota fiscal"
                                        style={{
                                            position: "absolute",
                                            right: "8px",
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                            background: "transparent",
                                            border: "none",
                                            color: "#EF4444",
                                            cursor: "pointer",
                                            fontSize: "18px",
                                            padding: "4px 8px"
                                        }}
                                    >
                                        ✕
                                    </button>
                                )}
                                
                                {/* Dropdown de resultados */}
                                {showNotasFiscaisDropdown && notasFiscais.length > 0 && (
                                    <div style={{
                                        position: "absolute",
                                        top: "100%",
                                        left: 0,
                                        right: 0,
                                        backgroundColor: "white",
                                        border: "1px solid #D1D5DB",
                                        borderRadius: "8px",
                                        marginTop: "4px",
                                        maxHeight: "300px",
                                        overflowY: "auto",
                                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                                        zIndex: 1000
                                    }}>
                                        {notasFiscais.map((nota) => (
                                            <div
                                                key={nota.id}
                                                onClick={() => selecionarNotaFiscal(nota)}
                                                style={{
                                                    padding: "12px",
                                                    borderBottom: "1px solid #E5E7EB",
                                                    cursor: "pointer",
                                                    transition: "background-color 0.2s"
                                                }}
                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#F3F4F6"}
                                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "white"}
                                            >
                                                <div style={{ fontWeight: "600", color: "#1F2937", marginBottom: "4px" }}>
                                                    NF {nota.numero}{nota.serie ? `/${nota.serie}` : ''}
                                                </div>
                                                <div style={{ fontSize: "12px", color: "#6B7280" }}>
                                                    {nota.fornecedor.nomeFornecedor}
                                                </div>
                                                <div style={{ fontSize: "12px", color: "#6B7280", marginTop: "2px" }}>
                                                    Data: {nota.dataEmissao ? new Date(nota.dataEmissao).toLocaleDateString('pt-BR') : 'N/A'} 
                                                    {nota.valorNfe && ` | Valor: R$ ${parseFloat(nota.valorNfe).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                
                                {!formData.fornecedorId && (
                                    <div style={{ fontSize: "12px", color: "#6B7280", marginTop: "4px" }}>
                                        Selecione um fornecedor primeiro para buscar notas fiscais
                                    </div>
                                )}
                            </div>
                        </div>

                        <div>
                            <label style={{ display: "block", fontSize: "14px", fontWeight: "500", marginBottom: "6px", color: "#374151" }}>
                                Obra *
                            </label>
                            <select
                                name="obraId"
                                value={formData.obraId}
                                onChange={handleInputChange}
                                required
                                style={{
                                    width: "100%",
                                    padding: "10px 12px",
                                    border: "1px solid #D1D5DB",
                                    borderRadius: "8px",
                                    fontSize: "14px"
                                }}
                            >
                                <option value="">Selecione uma obra</option>
                                {/* Para novo pedido (sem obra) oculta concluídas; em edição mantém a obra atual visível mesmo se concluída. */}
                                {obras
                                    .filter(o => {
                                        const ehConcluida = (o.status?.nome || "").toLowerCase() === "concluído";
                                        if (!ehConcluida) return true;
                                        return formData.obraId && Number(formData.obraId) === o.id;
                                    })
                                    .map(o => (
                                        <option key={o.id} value={o.id}>
                                            {o.nome || o.nomeProjeto}
                                        </option>
                                    ))}
                            </select>
                        </div>

                        <div style={{ gridColumn: "1 / -1" }}>
                            <label style={{ display: "block", fontSize: "14px", fontWeight: "500", marginBottom: "6px", color: "#374151" }}>
                                Escopo (Opcional)
                            </label>
                            <textarea
                                name="escopo"
                                value={formData.escopo || ""}
                                onChange={handleInputChange}
                                placeholder="Ex: Materiais para serralheria da obra para montagem da passarela metálica e mezanino do estacionamento"
                                style={{
                                    width: "100%",
                                    padding: "10px 12px",
                                    border: "1px solid #D1D5DB",
                                    borderRadius: "8px",
                                    fontSize: "14px",
                                    minHeight: "80px",
                                    resize: "vertical"
                                }}
                            />
                        </div>

                        <div>
                            <label style={{ display: "block", fontSize: "14px", fontWeight: "500", marginBottom: "6px", color: "#374151" }}>
                                Forma de Pagamento
                            </label>
                            <select
                                name="formaPagamento"
                                value={formData.formaPagamento}
                                onChange={handleInputChange}
                                style={{
                                    width: "100%",
                                    padding: "10px 12px",
                                    border: "1px solid #D1D5DB",
                                    borderRadius: "8px",
                                    fontSize: "14px"
                                }}
                            >
                                <option value="">Selecione</option>
                                <option value="BOLETO">Boleto</option>
                                <option value="PIX">PIX</option>
                                <option value="TRANSFERENCIA">Transferência Bancária</option>
                                <option value="DINHEIRO">Dinheiro</option>
                                <option value="CHEQUE">Cheque</option>
                                <option value="CARTAO">Cartão</option>
                            </select>

                            {formData.formaPagamento === "PIX" && (
                                <div style={{
                                    marginTop: "8px",
                                    padding: "10px 14px",
                                    backgroundColor: "#FEF3C7",
                                    border: "1px solid #F59E0B",
                                    borderRadius: "8px",
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: "8px"
                                }}>
                                    <span style={{ fontSize: "16px", flexShrink: 0 }}>⚠️</span>
                                    <p style={{ fontSize: "13px", color: "#92400E", margin: 0, lineHeight: "1.4" }}>
                                        <strong>Atenção:</strong> Esclarecemos que, para este pedido, pagamentos via PIX possuem taxa adicional, ao passo que pagamentos via boleto não possuem taxa.
                                    </p>
                                </div>
                            )}
                        </div>

                        <div>
                            <label style={{ display: "block", fontSize: "14px", fontWeight: "500", marginBottom: "6px", color: "#374151" }}>
                                Prazo de Entrega (dias)
                            </label>
                            <input
                                type="number"
                                name="prazoEntrega"
                                value={formData.prazoEntrega}
                                onChange={handleInputChange}
                                style={{
                                    width: "100%",
                                    padding: "10px 12px",
                                    border: "1px solid #D1D5DB",
                                    borderRadius: "8px",
                                    fontSize: "14px"
                                }}
                            />
                        </div>
                    </div>

                    <div style={{ marginTop: "16px" }}>
                        <label style={{ display: "block", fontSize: "14px", fontWeight: "500", marginBottom: "6px", color: "#374151" }}>
                            Condições de Pagamento
                        </label>
                        <input
                            type="text"
                            name="condicoesPagamento"
                            value={formData.condicoesPagamento}
                            onChange={handleInputChange}
                            placeholder="Ex: 30 dias após entrega"
                            style={{
                                width: "100%",
                                padding: "10px 12px",
                                border: "1px solid #D1D5DB",
                                borderRadius: "8px",
                                fontSize: "14px"
                            }}
                        />
                    </div>

                    {/* Componente de detalhes de pagamento dinâmico */}
                    <DetalhesPagamentoForm
                        formData={formData}
                        handleInputChange={handleInputChange}
                        disabled={statusEhNaoEditavel}
                    />
                </div>
                        </fieldset>

                    {/* Gerenciador de boletos parcelados - sempre habilitado */}
                    {formData.formaPagamento === "BOLETO" && 
                     formData.isPagamentoParcelado && 
                     formData.parcelamentoParcelas > 0 &&
                     formData.parcelamentoDataPrimeira &&
                     formData.parcelamentoIntervalo && (
                        <div style={{ marginTop: "24px" }}>
                            <BoletosParceladosManager
                                numeroParcelas={parseInt(formData.parcelamentoParcelas)}
                                dataVencimentoPrimeira={formData.parcelamentoDataPrimeira}
                                intervalo={formData.parcelamentoIntervalo}
                                onBoletosChange={handleBoletosChange}
                                disabled={false}
                                boletosExistentes={formData.boletosParcelados}
                            />
                        </div>
                    )}

                    <div style={{ marginTop: "16px" }}>
                        <label style={{ display: "block", fontSize: "14px", fontWeight: "500", marginBottom: "6px", color: "#374151" }}>
                            Observações
                        </label>
                        <textarea
                            name="observacoes"
                            value={formData.observacoes}
                            onChange={handleInputChange}
                            rows="3"
                            style={{
                                width: "100%",
                                padding: "10px 12px",
                                border: "1px solid #D1D5DB",
                                borderRadius: "8px",
                                fontSize: "14px",
                                resize: "vertical"
                            }}
                        />
                    </div>

                </div>
                )}
                {/* FIM ABA: DADOS GERAIS */}

                {/* ======================== ABA: ITENS DO PEDIDO ======================== */}
                {abaAtiva === "itens" && (
                    <div>
                        <fieldset disabled={statusEhNaoEditavel} style={{ border: 'none', padding: 0, margin: 0 }}>
                        <h2 style={{ 
                            fontSize: "20px", 
                            fontWeight: "600", 
                            marginBottom: "24px", 
                            color: "#1F2937",
                            borderBottom: "2px solid #E5E7EB",
                            paddingBottom: "12px"
                        }}>
                            📦 Itens do Pedido
                            <span style={{
                                marginLeft: "12px",
                                fontSize: "14px",
                                fontWeight: "400",
                                color: "#6B7280"
                            }}>
                                ({formData.itens.length} {formData.itens.length === 1 ? 'item' : 'itens'})
                            </span>
                        </h2>

                {/* Itens do Pedido */}
                <div style={{
                    backgroundColor: "transparent"
                }}>
                    <h2 style={{ fontSize: "18px", fontWeight: "600", marginBottom: "20px", color: "#1F2937" }}>
                        Itens do Pedido *
                    </h2>

                    {/* Formulário de Novo Item */}
                    <div style={{
                        backgroundColor: "#F9FAFB",
                        padding: "16px",
                        borderRadius: "8px",
                        marginBottom: "20px"
                    }}>
                        <div className="form-item-add-header" style={{ 
                            display: "flex", 
                            justifyContent: "space-between", 
                            alignItems: "center",
                            marginBottom: "12px" 
                        }}>
                            <h3 style={{ fontSize: "14px", fontWeight: "600", margin: 0, color: "#374151" }}>
                                Adicionar Item
                            </h3>
                            
                            {/* Botões de Importar e Baixar Template */}
                            <div className="form-import-btns" style={{ display: "flex", gap: "8px" }}>
                                <button
                                    type="button"
                                    onClick={downloadTemplateExcel}
                                    style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "6px",
                                        padding: "8px 16px",
                                        backgroundColor: "#6366F1",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "6px",
                                        cursor: "pointer",
                                        fontSize: "13px",
                                        fontWeight: "600",
                                        whiteSpace: "nowrap"
                                    }}
                                >
                                    📥 Baixar Template
                                </button>
                                
                                <input
                                    type="file"
                                    id="import-excel"
                                    accept=".xlsx,.xls"
                                    onChange={handleImportarExcel}
                                    style={{ display: "none" }}
                                />
                                <label
                                    htmlFor="import-excel"
                                    style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "6px",
                                        padding: "8px 16px",
                                        backgroundColor: "#10B981",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "6px",
                                        cursor: "pointer",
                                        fontSize: "13px",
                                        fontWeight: "600",
                                        whiteSpace: "nowrap"
                                    }}
                                >
                                    📊 Importar Excel
                                </label>
                            </div>
                        </div>

                        <div className="form-item-add-grid" style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr auto", gap: "12px", alignItems: "end" }}>
                            <div>
                                <label style={{ display: "block", fontSize: "12px", fontWeight: "500", marginBottom: "4px", color: "#6B7280" }}>
                                    Descrição *
                                </label>
                                <input
                                    type="text"
                                    name="descricao"
                                    value={novoItem.descricao}
                                    onChange={handleNovoItemChange}
                                    style={{
                                        width: "100%",
                                        padding: "8px 10px",
                                        border: "1px solid #D1D5DB",
                                        borderRadius: "6px",
                                        fontSize: "14px"
                                    }}
                                />
                            </div>

                            <div>
                                <label style={{ display: "block", fontSize: "12px", fontWeight: "500", marginBottom: "4px", color: "#6B7280" }}>
                                    Quantidade *
                                </label>
                                <input
                                    type="number"
                                    name="quantidade"
                                    value={novoItem.quantidade}
                                    onChange={handleNovoItemChange}
                                    style={{
                                        width: "100%",
                                        padding: "8px 10px",
                                        border: "1px solid #D1D5DB",
                                        borderRadius: "6px",
                                        fontSize: "14px"
                                    }}
                                />
                            </div>

                            <div>
                                <label style={{ display: "block", fontSize: "12px", fontWeight: "500", marginBottom: "4px", color: "#6B7280" }}>
                                    Unidade
                                </label>
                                <select
                                    name="unidade"
                                    value={novoItem.unidade}
                                    onChange={handleNovoItemChange}
                                    style={{
                                        width: "100%",
                                        padding: "8px 10px",
                                        border: "1px solid #D1D5DB",
                                        borderRadius: "6px",
                                        fontSize: "14px"
                                    }}
                                >
                                    <option value="UN">UN</option>
                                    <option value="KG">KG</option>
                                    <option value="MT">MT</option>
                                    <option value="CX">CX</option>
                                    <option value="PC">PC</option>
                                    <option value="LT">LT</option>
                                </select>
                            </div>

                            <div>
                                <label style={{ display: "block", fontSize: "12px", fontWeight: "500", marginBottom: "4px", color: "#6B7280" }}>
                                    Preço Unit. *
                                </label>
                                <input
                                    type="text"
                                    name="precoUnitario"
                                    value={novoItem.precoUnitario}
                                    onChange={handleNovoItemChange}
                                    style={{
                                        width: "100%",
                                        padding: "8px 10px",
                                        border: "1px solid #D1D5DB",
                                        borderRadius: "6px",
                                        fontSize: "14px"
                                    }}
                                />
                            </div>

                            <div>
                                <label style={{ display: "block", fontSize: "12px", fontWeight: "500", marginBottom: "4px", color: "#6B7280" }}>
                                    Total
                                </label>
                                <input
                                    type="text"
                                    value={maskCurrency(Math.round((novoItem.valorTotal || 0) * 100))}
                                    disabled
                                    style={{
                                        width: "100%",
                                        padding: "8px 10px",
                                        border: "1px solid #D1D5DB",
                                        borderRadius: "6px",
                                        fontSize: "14px",
                                        backgroundColor: "#F3F4F6"
                                    }}
                                />
                            </div>

                            <button
                                onClick={adicionarItem}
                                style={{
                                    padding: "8px 16px",
                                    backgroundColor: "#3B82F6",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "6px",
                                    cursor: "pointer",
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    whiteSpace: "nowrap"
                                }}
                            >
                                Adicionar
                            </button>
                        </div>
                    </div>

                    {/* Tabela de Itens */}
                    {formData.itens.length > 0 && (
                        <div style={{ overflowX: "auto" }}>
                            <table className="form-itens-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                                <thead>
                                    <tr style={{ backgroundColor: "#F9FAFB", borderBottom: "2px solid #E5E7EB" }}>
                                        <th style={{ padding: "12px", textAlign: "left", fontSize: "12px", fontWeight: "600", color: "#6B7280", textTransform: "uppercase" }}>
                                            Descrição
                                        </th>
                                        <th style={{ padding: "12px", textAlign: "center", fontSize: "12px", fontWeight: "600", color: "#6B7280", textTransform: "uppercase" }}>
                                            Quantidade
                                        </th>
                                        <th style={{ padding: "12px", textAlign: "center", fontSize: "12px", fontWeight: "600", color: "#6B7280", textTransform: "uppercase" }}>
                                            Unidade
                                        </th>
                                        <th style={{ padding: "12px", textAlign: "right", fontSize: "12px", fontWeight: "600", color: "#6B7280", textTransform: "uppercase" }}>
                                            Preço Unit.
                                        </th>
                                        <th style={{ padding: "12px", textAlign: "right", fontSize: "12px", fontWeight: "600", color: "#6B7280", textTransform: "uppercase" }}>
                                            Total
                                        </th>
                                        <th style={{ padding: "12px", textAlign: "center", fontSize: "12px", fontWeight: "600", color: "#6B7280", textTransform: "uppercase" }}>
                                            Ações
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {formData.itens.map((item, index) => (
                                        <tr key={item.id || index} style={{ borderBottom: "1px solid #E5E7EB", backgroundColor: item.editando ? "#FEF3C7" : "transparent" }}>
                                            <td style={{ padding: "12px", fontSize: "14px", color: "#111827" }}>
                                                {item.editando ? (
                                                    <input
                                                        type="text"
                                                        value={item.descricao}
                                                        onChange={(e) => atualizarItemEdicao(index, 'descricao', e.target.value)}
                                                        style={{
                                                            width: "100%",
                                                            padding: "6px 8px",
                                                            border: "1px solid #D1D5DB",
                                                            borderRadius: "4px",
                                                            fontSize: "14px"
                                                        }}
                                                    />
                                                ) : (
                                                    item.descricao
                                                )}
                                            </td>
                                            <td style={{ padding: "12px", textAlign: "center", fontSize: "14px", color: "#111827" }}>
                                                {item.editando ? (
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={item.quantidade}
                                                        onChange={(e) => atualizarItemEdicao(index, 'quantidade', e.target.value)}
                                                        style={{
                                                            width: "80px",
                                                            padding: "6px 8px",
                                                            border: "1px solid #D1D5DB",
                                                            borderRadius: "4px",
                                                            fontSize: "14px",
                                                            textAlign: "center"
                                                        }}
                                                    />
                                                ) : (
                                                    item.quantidade
                                                )}
                                            </td>
                                            <td style={{ padding: "12px", textAlign: "center", fontSize: "14px", color: "#111827" }}>
                                                {item.editando ? (
                                                    <select
                                                        value={item.unidade}
                                                        onChange={(e) => atualizarItemEdicao(index, 'unidade', e.target.value)}
                                                        style={{
                                                            width: "80px",
                                                            padding: "6px 8px",
                                                            border: "1px solid #D1D5DB",
                                                            borderRadius: "4px",
                                                            fontSize: "14px"
                                                        }}
                                                    >
                                                        <option value="UN">UN</option>
                                                        <option value="KG">KG</option>
                                                        <option value="MT">MT</option>
                                                        <option value="CX">CX</option>
                                                        <option value="PC">PC</option>
                                                        <option value="LT">LT</option>
                                                    </select>
                                                ) : (
                                                    item.unidade
                                                )}
                                            </td>
                                            <td style={{ padding: "12px", textAlign: "right", fontSize: "14px", color: "#111827" }}>
                                                {item.editando ? (
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={item.precoUnitario}
                                                        onChange={(e) => atualizarItemEdicao(index, 'precoUnitario', e.target.value)}
                                                        style={{
                                                            width: "120px",
                                                            padding: "6px 8px",
                                                            border: "1px solid #D1D5DB",
                                                            borderRadius: "4px",
                                                            fontSize: "14px",
                                                            textAlign: "right"
                                                        }}
                                                    />
                                                ) : (
                                                    (item.precoUnitario || 0).toLocaleString('pt-BR', { 
                                                        style: 'currency', 
                                                        currency: 'BRL' 
                                                    })
                                                )}
                                            </td>
                                            <td style={{ padding: "12px", textAlign: "right", fontSize: "14px", fontWeight: "600", color: "#111827" }}>
                                                {(item.valorTotal || 0).toLocaleString('pt-BR', { 
                                                    style: 'currency', 
                                                    currency: 'BRL' 
                                                })}
                                            </td>
                                            <td style={{ padding: "12px", textAlign: "center" }}>
                                                {item.editando ? (
                                                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "8px" }}>
                                                        <button
                                                            onClick={() => salvarEdicaoItem(index)}
                                                            title="Salvar alterações"
                                                            style={{
                                                                backgroundColor: "#10B981",
                                                                color: "white",
                                                                border: "none",
                                                                borderRadius: "4px",
                                                                padding: "6px 12px",
                                                                cursor: "pointer",
                                                                fontSize: "14px",
                                                                fontWeight: "600"
                                                            }}
                                                        >
                                                            ✓
                                                        </button>
                                                        <button
                                                            onClick={() => cancelarEdicaoItem(index)}
                                                            title="Cancelar edição"
                                                            style={{
                                                                backgroundColor: "#6B7280",
                                                                color: "white",
                                                                border: "none",
                                                                borderRadius: "4px",
                                                                padding: "6px 12px",
                                                                cursor: "pointer",
                                                                fontSize: "14px",
                                                                fontWeight: "600"
                                                            }}
                                                        >
                                                            ✕
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "8px" }}>
                                                        <img
                                                            src="/images/icon/edit.png"
                                                            alt="Editar"
                                                            title="Editar item"
                                                            onClick={() => editarItem(index)}
                                                            style={{
                                                                width: "20px",
                                                                height: "20px",
                                                                cursor: "pointer"
                                                            }}
                                                        />
                                                        <img
                                                            src="/images/icon/trash.png"
                                                            alt="Remover"
                                                            title="Remover item"
                                                            onClick={() => removerItem(item.id)}
                                                            style={{
                                                                width: "20px",
                                                                height: "20px",
                                                                cursor: "pointer"
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr style={{ backgroundColor: "#F9FAFB" }}>
                                        <td colSpan="4" style={{ padding: "12px", textAlign: "right", fontSize: "14px", color: "#6B7280" }}>
                                            Subtotal:
                                        </td>
                                        <td style={{ padding: "12px", textAlign: "right", fontSize: "14px", fontWeight: "600", color: "#1F2937" }}>
                                            {calcularSubtotal().toLocaleString('pt-BR', { 
                                                style: 'currency', 
                                                currency: 'BRL' 
                                            })}
                                        </td>
                                        <td></td>
                                    </tr>
                                    <tr style={{ backgroundColor: "white" }}>
                                        <td colSpan="4" style={{ padding: "12px", textAlign: "right", fontSize: "14px", color: "#6B7280" }}>
                                            Desconto:
                                        </td>
                                        <td style={{ padding: "12px", textAlign: "right" }}>
                                            <input
                                                type="text"
                                                name="valorDesconto"
                                                value={formData.valorDescontoFormatted || ''}
                                                onChange={(e) => {
                                                    const maskedValue = maskCurrency(e.target.value);
                                                    const numericValue = parseCurrencyToFloat(maskedValue);
                                                    setFormData(prev => ({ 
                                                        ...prev, 
                                                        valorDesconto: numericValue,
                                                        valorDescontoFormatted: maskedValue
                                                    }));
                                                }}
                                                style={{
                                                    width: "120px",
                                                    padding: "6px 8px",
                                                    border: "1px solid #D1D5DB",
                                                    borderRadius: "4px",
                                                    fontSize: "14px",
                                                    textAlign: "right"
                                                }}
                                                placeholder="R$ 0,00"
                                            />
                                        </td>
                                        <td></td>
                                    </tr>
                                    <tr style={{ backgroundColor: "white" }}>
                                        <td colSpan="4" style={{ padding: "12px", textAlign: "right", fontSize: "14px", color: "#6B7280" }}>
                                            Frete:
                                        </td>
                                        <td style={{ padding: "12px", textAlign: "right" }}>
                                            <input
                                                type="text"
                                                name="valorFrete"
                                                value={formData.valorFreteFormatted || ''}
                                                onChange={(e) => {
                                                    const maskedValue = maskCurrency(e.target.value);
                                                    const numericValue = parseCurrencyToFloat(maskedValue);
                                                    setFormData(prev => ({ 
                                                        ...prev, 
                                                        valorFrete: numericValue,
                                                        valorFreteFormatted: maskedValue
                                                    }));
                                                }}
                                                style={{
                                                    width: "120px",
                                                    padding: "6px 8px",
                                                    border: "1px solid #D1D5DB",
                                                    borderRadius: "4px",
                                                    fontSize: "14px",
                                                    textAlign: "right"
                                                }}
                                                placeholder="R$ 0,00"
                                            />
                                        </td>
                                        <td></td>
                                    </tr>
                                    <tr style={{ backgroundColor: "#EFF6FF", fontWeight: "bold" }}>
                                        <td colSpan="4" style={{ padding: "12px", textAlign: "right", fontSize: "16px", color: "#1F2937" }}>
                                            Valor Final:
                                        </td>
                                        <td style={{ padding: "12px", textAlign: "right", fontSize: "18px", fontWeight: "700", color: "#1E40AF" }}>
                                            {calcularValorFinal().toLocaleString('pt-BR', { 
                                                style: 'currency', 
                                                currency: 'BRL' 
                                            })}
                                        </td>
                                        <td></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}

                    {formData.itens.length === 0 && (
                        <div style={{
                            textAlign: "center",
                            padding: "40px",
                            color: "#6B7280",
                            fontSize: "14px"
                        }}>
                            Nenhum item adicionado. Adicione itens ao pedido usando o formulário acima.
                        </div>
                    )}
                </div>
                
                        </fieldset>
                </div>
                )}
                {/* FIM ABA: ITENS */}

                {/* ======================== ABA: ANEXOS ======================== */}
                {abaAtiva === "anexos" && (
                    <div>
                        <h2 style={{ 
                            fontSize: "20px", 
                            fontWeight: "600", 
                            marginBottom: "24px", 
                            color: "#1F2937",
                            borderBottom: "2px solid #E5E7EB",
                            paddingBottom: "12px"
                        }}>
                            📎 Anexos e Documentos
                            <span style={{
                                marginLeft: "12px",
                                fontSize: "14px",
                                fontWeight: "400",
                                color: "#6B7280"
                            }}>
                                ({anexosSelecionados.length} {anexosSelecionados.length === 1 ? 'arquivo' : 'arquivos'})
                            </span>
                        </h2>

                        {/* Copiar aqui todo o conteúdo da seção de Anexos que está dentro de "Dados Gerais" */}
                        <div style={{ marginTop: "16px" }}>
                            <h3 style={{ fontSize: "16px", fontWeight: "600", marginBottom: "16px", color: "#374151" }}>
                                Adicionar Novo Anexo
                            </h3>
                            
                            <div className="form-anexo-add-grid" style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "12px", marginBottom: "12px" }}>
                                <div>
                                    <label style={{ display: "block", fontSize: "14px", fontWeight: "500", marginBottom: "6px", color: "#374151" }}>
                                        Tipo de Documento
                                    </label>
                                    <select
                                        value={tipoDocumentoAnexo}
                                        onChange={(e) => setTipoDocumentoAnexo(e.target.value)}
                                        style={{
                                            width: "100%",
                                            padding: "10px 12px",
                                            border: "1px solid #D1D5DB",
                                            borderRadius: "8px",
                                            fontSize: "14px"
                                        }}
                                    >
                                        <option value="">Selecionar</option>
                                        {tiposDocumentos.map((tipo) => (
                                            <option key={tipo.id} value={tipo.id}>
                                                {tipo.nome}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                
                                <div>
                                    <label style={{ display: "block", fontSize: "14px", fontWeight: "500", marginBottom: "6px", color: "#374151" }}>
                                        Selecionar Documento
                                    </label>
                                    <div style={{ display: "flex", gap: "8px" }}>
                                        <div style={{ position: "relative", flex: 1 }}>
                                            <input
                                                type="file"
                                                accept=".pdf,.doc,.docx,.xls,.xlsx"
                                                onChange={handleAnexoFileChange}
                                                style={{
                                                    position: "absolute",
                                                    inset: 0,
                                                    opacity: 0,
                                                    cursor: "pointer"
                                                }}
                                            />
                                            <div style={{
                                                width: "100%",
                                                padding: "10px 12px",
                                                border: "1px solid #D1D5DB",
                                                borderRadius: "8px",
                                                fontSize: "14px",
                                                color: "#9CA3AF"
                                            }}>
                                                {anexoFile ? anexoFile.name : "Pesquisar Documento"}
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleAddAnexo}
                                            style={{
                                                padding: "10px 20px",
                                                backgroundColor: "#1E78B4",
                                                color: "white",
                                                border: "none",
                                                borderRadius: "8px",
                                                cursor: "pointer",
                                                fontWeight: "500",
                                                fontSize: "14px"
                                            }}
                                        >
                                            Adicionar
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {anexosSelecionados.length > 0 && (
                                <div style={{ marginTop: "16px" }}>
                                    <h4 style={{ fontSize: "14px", fontWeight: "600", marginBottom: "8px", color: "#374151" }}>
                                        Arquivos Selecionados
                                    </h4>
                                    <table className="form-anexos-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                                        <thead>
                                            <tr style={{ backgroundColor: "#F9FAFB", borderBottom: "2px solid #E5E7EB" }}>
                                                <th style={{ padding: "10px", textAlign: "left" }}>Tipo</th>
                                                <th style={{ padding: "10px", textAlign: "left" }}>Arquivo</th>
                                                <th style={{ padding: "10px", textAlign: "center", width: "100px" }}>Ações</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {anexosSelecionados.map((anexo, index) => (
                                                <tr key={index} style={{ borderBottom: "1px solid #E5E7EB" }}>
                                                    <td style={{ padding: "10px" }}>
                                                        {anexo.tipoNome || tiposDocumentos.find(t => t.id == anexo.tipoDocumentoId)?.nome || 'N/A'}
                                                    </td>
                                                    <td style={{ padding: "10px" }}>
                                                        {anexo.nomeArquivo || anexo.arquivo?.name || anexo.file?.name}
                                                        {anexo.urlArquivo && (
                                                            <a
                                                                href={anexo.urlArquivo}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={{ marginLeft: "10px", color: "#1E78B4" }}
                                                            >
                                                                (Visualizar)
                                                            </a>
                                                        )}
                                                    </td>
                                                    <td style={{ padding: "10px", textAlign: "center" }}>
                                                        <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                                            <img
                                                                src="/images/icon/trash.png"
                                                                alt="Remover"
                                                                title="Remover anexo"
                                                                onClick={() => handleRemoveAnexo(anexo.id)}
                                                                style={{
                                                                    width: "20px",
                                                                    height: "20px",
                                                                    cursor: "pointer"
                                                                }}
                                                            />
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                )}
                {/* FIM ABA: ANEXOS */}

                {/* ======================== ABA: IMPOSTOS RETIDOS ======================== */}
                {abaAtiva === "retencoes" && (
                    <div>
                        <fieldset disabled={statusEhNaoEditavel} style={{ border: 'none', padding: 0, margin: 0 }}>
                        <h2 style={{
                            fontSize: "20px",
                            fontWeight: "600",
                            marginBottom: "8px",
                            color: "#1F2937",
                            borderBottom: "2px solid #E5E7EB",
                            paddingBottom: "12px"
                        }}>
                            💰 Impostos Retidos
                        </h2>
                        <p style={{ color: "#6B7280", fontSize: "13px", marginBottom: "20px" }}>
                            Lançamento manual de retenções aplicadas ao pedido. O valor líquido ao fornecedor é
                            o valor final menos as retenções.
                        </p>

                        <button
                            type="button"
                            onClick={adicionarRetencao}
                            disabled={statusEhNaoEditavel}
                            style={{
                                marginBottom: "16px",
                                backgroundColor: "#1E78B4",
                                color: "white",
                                border: "none",
                                borderRadius: "6px",
                                padding: "10px 16px",
                                cursor: statusEhNaoEditavel ? "not-allowed" : "pointer",
                                fontWeight: "600"
                            }}
                        >
                            + Adicionar retenção
                        </button>

                        {formData.retencoes.length === 0 ? (
                            <div style={{ padding: "24px", textAlign: "center", color: "#9CA3AF", border: "1px dashed #D1D5DB", borderRadius: "8px" }}>
                                Nenhuma retenção adicionada.
                            </div>
                        ) : (
                            <div style={{ overflowX: "auto" }}>
                                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                                    <thead>
                                        <tr style={{ backgroundColor: "#F3F4F6" }}>
                                            <th style={{ padding: "10px", textAlign: "left", border: "1px solid #E5E7EB" }}>Tipo de Imposto *</th>
                                            <th style={{ padding: "10px", textAlign: "right", border: "1px solid #E5E7EB", width: "110px" }}>Alíquota (%)</th>
                                            <th style={{ padding: "10px", textAlign: "right", border: "1px solid #E5E7EB", width: "150px" }}>Base de Cálculo</th>
                                            <th style={{ padding: "10px", textAlign: "right", border: "1px solid #E5E7EB", width: "150px" }}>Valor Retido *</th>
                                            <th style={{ padding: "10px", textAlign: "left", border: "1px solid #E5E7EB" }}>Observação</th>
                                            <th style={{ padding: "10px", border: "1px solid #E5E7EB", width: "60px" }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {formData.retencoes.map((r, idx) => (
                                            <tr key={r.id || `new-${idx}`}>
                                                <td style={{ padding: "6px", border: "1px solid #E5E7EB" }}>
                                                    <select
                                                        value={r.tipoImpostoId || ""}
                                                        onChange={(e) => atualizarRetencao(idx, 'tipoImpostoId', e.target.value)}
                                                        disabled={statusEhNaoEditavel}
                                                        style={{ width: "100%", padding: "8px", border: "1px solid #D1D5DB", borderRadius: "4px" }}
                                                    >
                                                        <option value="">-- selecione --</option>
                                                        {tiposImposto.map(t => (
                                                            <option key={t.id} value={t.id}>{t.nome}</option>
                                                        ))}
                                                    </select>
                                                </td>
                                                <td style={{ padding: "6px", border: "1px solid #E5E7EB" }}>
                                                    <input
                                                        type="number"
                                                        step="0.0001"
                                                        min="0"
                                                        value={r.aliquota}
                                                        onChange={(e) => atualizarRetencao(idx, 'aliquota', e.target.value)}
                                                        disabled={statusEhNaoEditavel}
                                                        style={{ width: "100%", padding: "8px", border: "1px solid #D1D5DB", borderRadius: "4px", textAlign: "right" }}
                                                    />
                                                </td>
                                                <td style={{ padding: "6px", border: "1px solid #E5E7EB" }}>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        min="0"
                                                        value={r.baseCalculo}
                                                        onChange={(e) => atualizarRetencao(idx, 'baseCalculo', e.target.value)}
                                                        disabled={statusEhNaoEditavel}
                                                        style={{ width: "100%", padding: "8px", border: "1px solid #D1D5DB", borderRadius: "4px", textAlign: "right" }}
                                                    />
                                                </td>
                                                <td style={{ padding: "6px", border: "1px solid #E5E7EB" }}>
                                                    <input
                                                        type="text"
                                                        value={r.valorFormatted || ""}
                                                        onChange={(e) => atualizarRetencao(idx, 'valorFormatted', e.target.value)}
                                                        disabled={statusEhNaoEditavel}
                                                        placeholder="R$ 0,00"
                                                        style={{ width: "100%", padding: "8px", border: "1px solid #D1D5DB", borderRadius: "4px", textAlign: "right" }}
                                                    />
                                                </td>
                                                <td style={{ padding: "6px", border: "1px solid #E5E7EB" }}>
                                                    <input
                                                        type="text"
                                                        value={r.observacao || ""}
                                                        onChange={(e) => atualizarRetencao(idx, 'observacao', e.target.value)}
                                                        disabled={statusEhNaoEditavel}
                                                        style={{ width: "100%", padding: "8px", border: "1px solid #D1D5DB", borderRadius: "4px" }}
                                                    />
                                                </td>
                                                <td style={{ padding: "6px", border: "1px solid #E5E7EB", textAlign: "center" }}>
                                                    <button
                                                        type="button"
                                                        onClick={() => removerRetencao(idx)}
                                                        disabled={statusEhNaoEditavel}
                                                        title="Remover"
                                                        style={{
                                                            backgroundColor: "#DC2626",
                                                            color: "white",
                                                            border: "none",
                                                            borderRadius: "4px",
                                                            padding: "6px 10px",
                                                            cursor: statusEhNaoEditavel ? "not-allowed" : "pointer"
                                                        }}
                                                    >
                                                        ×
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Resumo */}
                        <div style={{
                            marginTop: "20px",
                            padding: "16px",
                            backgroundColor: "#F9FAFB",
                            border: "1px solid #E5E7EB",
                            borderRadius: "8px",
                            display: "flex",
                            justifyContent: "space-between",
                            gap: "16px",
                            flexWrap: "wrap"
                        }}>
                            <div>
                                <div style={{ fontSize: "12px", color: "#6B7280" }}>Valor Final do Pedido</div>
                                <div style={{ fontSize: "16px", fontWeight: "600" }}>
                                    {calcularValorFinal().toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                </div>
                            </div>
                            <div>
                                <div style={{ fontSize: "12px", color: "#6B7280" }}>Total de Retenções</div>
                                <div style={{ fontSize: "16px", fontWeight: "600", color: "#DC2626" }}>
                                    − {calcularTotalRetencoes().toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                </div>
                            </div>
                            <div>
                                <div style={{ fontSize: "12px", color: "#6B7280" }}>Valor Líquido ao Fornecedor</div>
                                <div style={{ fontSize: "18px", fontWeight: "700", color: "#059669" }}>
                                    {(calcularValorFinal() - calcularTotalRetencoes()).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                </div>
                            </div>
                        </div>

                        {calcularTotalRetencoes() > calcularValorFinal() && (
                            <div style={{ marginTop: "12px", padding: "10px", backgroundColor: "#FEE2E2", border: "1px solid #FCA5A5", borderRadius: "6px", color: "#991B1B" }}>
                                ⚠ A soma das retenções excede o valor final do pedido. Ajuste antes de salvar.
                            </div>
                        )}
                        </fieldset>
                    </div>
                )}
                {/* FIM ABA: IMPOSTOS RETIDOS */}

                {/* ======================== ABA: OBSERVAÇÕES ======================== */}
                {abaAtiva === "obs" && (
                    <div>
                        <h2 style={{ 
                            fontSize: "20px", 
                            fontWeight: "600", 
                            marginBottom: "24px", 
                            color: "#1F2937",
                            borderBottom: "2px solid #E5E7EB",
                            paddingBottom: "12px"
                        }}>
                            📝 Observações Gerais
                        </h2>

                        <div>
                            <label style={{ 
                                display: "block", 
                                fontSize: "14px", 
                                fontWeight: "500", 
                                marginBottom: "8px", 
                                color: "#374151" 
                            }}>
                                Observações do Pedido
                            </label>
                            <textarea
                                name="observacoes"
                                value={formData.observacoes}
                                onChange={handleInputChange}
                                rows="20"
                                placeholder="Digite aqui observações gerais sobre o pedido de compra..."
                                style={{
                                    width: "100%",
                                    padding: "16px",
                                    border: "1px solid #D1D5DB",
                                    borderRadius: "8px",
                                    fontSize: "14px",
                                    resize: "vertical",
                                    fontFamily: "inherit",
                                    lineHeight: "1.6"
                                }}
                            />
                            <p style={{
                                fontSize: "12px",
                                color: "#9CA3AF",
                                marginTop: "8px"
                            }}>
                                {formData.observacoes?.length || 0} caracteres
                            </p>
                        </div>
                    </div>
                )}
                {/* FIM ABA: OBSERVAÇÕES */}

                </div>
                {/* FIM Container de Abas */}

                {/* Botões de Ação */}
                <div className="form-action-buttons" style={{
                    display: "flex",
                    gap: "12px",
                    justifyContent: "flex-end",
                    marginTop: "24px"
                }}>
                    <button
                        onClick={() => navigate("/pedidos-compra")}
                        style={{
                            padding: "10px 24px",
                            backgroundColor: "#F3F4F6",
                            color: "#374151",
                            border: "none",
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontSize: "14px",
                            fontWeight: "600"
                        }}
                    >
                        Cancelar
                    </button>

                    {isEditMode && podeDeletar && formData.status === "RASCUNHO" && (
                        <button
                            onClick={handleDeletar}
                            style={{
                                padding: "10px 24px",
                                backgroundColor: "#EF4444",
                                color: "white",
                                border: "none",
                                borderRadius: "8px",
                                cursor: "pointer",
                                fontSize: "14px",
                                fontWeight: "600"
                            }}
                        >
                            Excluir
                        </button>
                    )}

                    <button
                        onClick={handleSubmit}
                        style={{
                            padding: "10px 24px",
                            backgroundColor: "#10B981",
                            color: "white",
                            border: "none",
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontSize: "14px",
                            fontWeight: "600"
                        }}
                    >
                        {statusEhNaoEditavel
                            ? "Salvar Boletos/Anexos"
                            : isEditMode ? "Atualizar" : "Salvar Rascunho"}
                    </button>

                    {(!isEditMode || formData.status === "RASCUNHO") && (
                        <button
                            onClick={handleEnviarParaOrcamento}
                            style={{
                                padding: "10px 24px",
                                backgroundColor: "#F97316",
                                color: "white",
                                border: "none",
                                borderRadius: "8px",
                                cursor: "pointer",
                                fontSize: "14px",
                                fontWeight: "600"
                            }}
                        >
                            Enviar para Orçamento
                        </button>
                    )}

                    {(!isEditMode || formData.status === "RASCUNHO") && (
                        <button
                            onClick={handleEnviarAprovacao}
                            style={{
                                padding: "10px 24px",
                                backgroundColor: "#3B82F6",
                                color: "white",
                                border: "none",
                                borderRadius: "8px",
                                cursor: "pointer",
                                fontSize: "14px",
                                fontWeight: "600"
                            }}
                        >
                            Enviar para Aprovação
                        </button>
                    )}
                </div>

                {/* Modais */}
                <ConfirmationModal
                    isVisible={showConfirmDialog}
                    onCancel={() => setShowConfirmDialog(false)}
                    onConfirm={confirmarSalvamento}
                    message={`Deseja realmente ${isEditMode ? 'atualizar' : 'cadastrar'} este pedido de compra?`}
                    textButtonConfirm={isEditMode ? "Atualizar" : "Cadastrar"}
                />

                <ConfirmationModal
                    isVisible={showConfirmDeleteRegister}
                    onCancel={() => setShowConfirmDeleteRegister(false)}
                    onConfirm={confirmarDelecao}
                    message="Tem certeza que deseja excluir este pedido? Esta ação não pode ser desfeita."
                    textButtonConfirm="Excluir"
                />

                <SuccessModal
                    isVisible={showSuccessDialog}
                    onClose={handleSuccessClose}
                    message={successMessage}
                />

                {/* Modal: Cancelar Pedido */}
                {showCancelarDialog && (
                    <div style={{
                        position: "fixed",
                        inset: 0,
                        backgroundColor: "rgba(0,0,0,0.5)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1000
                    }}>
                        <div style={{
                            backgroundColor: "white",
                            borderRadius: "12px",
                            padding: "24px",
                            width: "min(520px, 90vw)",
                            boxShadow: "0 20px 60px rgba(0,0,0,0.2)"
                        }}>
                            <h3 style={{ fontSize: "18px", fontWeight: "700", marginBottom: "8px", color: "#991B1B" }}>
                                🚫 Cancelar Pedido de Compra
                            </h3>
                            <p style={{ fontSize: "14px", color: "#4B5563", marginBottom: "16px" }}>
                                Esta ação muda o status para <strong>Cancelada</strong> e fica registrada no histórico de auditoria.
                                Informe o <strong>motivo</strong> do cancelamento.
                            </p>
                            <textarea
                                value={motivoCancelamento}
                                onChange={(e) => setMotivoCancelamento(e.target.value)}
                                placeholder="Motivo do cancelamento (obrigatório)"
                                rows={4}
                                style={{
                                    width: "100%",
                                    padding: "10px",
                                    border: "1px solid #D1D5DB",
                                    borderRadius: "6px",
                                    fontSize: "14px",
                                    resize: "vertical",
                                    fontFamily: "inherit"
                                }}
                            />
                            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "16px" }}>
                                <button
                                    type="button"
                                    onClick={() => { setShowCancelarDialog(false); setMotivoCancelamento(""); }}
                                    disabled={cancelando}
                                    style={{
                                        padding: "10px 18px",
                                        border: "1px solid #D1D5DB",
                                        backgroundColor: "white",
                                        borderRadius: "6px",
                                        cursor: cancelando ? "not-allowed" : "pointer",
                                        fontWeight: "500"
                                    }}
                                >
                                    Voltar
                                </button>
                                <button
                                    type="button"
                                    onClick={confirmarCancelamento}
                                    disabled={cancelando || !motivoCancelamento.trim()}
                                    style={{
                                        padding: "10px 18px",
                                        border: "none",
                                        backgroundColor: !motivoCancelamento.trim() ? "#FCA5A5" : "#DC2626",
                                        color: "white",
                                        borderRadius: "6px",
                                        cursor: cancelando || !motivoCancelamento.trim() ? "not-allowed" : "pointer",
                                        fontWeight: "600"
                                    }}
                                >
                                    {cancelando ? "Cancelando..." : "Confirmar Cancelamento"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <style>
                {`
                    @media (max-width: 640px) {
                        .cadastrar-ata-page {
                            padding: 4px !important;
                            overflow-x: hidden !important;
                        }

                        .form-titulo {
                            font-size: 18px !important;
                            word-break: break-word !important;
                        }

                        .header-section {
                            margin-bottom: 16px !important;
                        }

                        .form-tab-content {
                            padding: 12px !important;
                            min-height: auto !important;
                            border-radius: 0 0 8px 8px !important;
                        }

                        /* Grids de campos */
                        .form-fields-grid {
                            grid-template-columns: 1fr !important;
                            gap: 12px !important;
                        }

                        /* Adicionar Item header */
                        .form-item-add-header {
                            flex-direction: column !important;
                            align-items: stretch !important;
                            gap: 10px !important;
                        }

                        .form-import-btns {
                            width: 100% !important;
                        }

                        .form-import-btns button,
                        .form-import-btns label {
                            flex: 1 !important;
                            text-align: center !important;
                            justify-content: center !important;
                            font-size: 12px !important;
                            padding: 8px 10px !important;
                        }

                        /* Grid de adicionar item */
                        .form-item-add-grid {
                            grid-template-columns: 1fr 1fr !important;
                            gap: 10px !important;
                        }

                        .form-item-add-grid > div:first-child {
                            grid-column: 1 / -1 !important;
                        }

                        .form-item-add-grid > button {
                            grid-column: 1 / -1 !important;
                            width: 100% !important;
                        }

                        /* Tabela de itens */
                        .form-itens-table thead {
                            display: none !important;
                        }

                        .form-itens-table tbody tr {
                            display: block !important;
                            background: #F9FAFB !important;
                            border: 1px solid #E5E7EB !important;
                            border-radius: 8px !important;
                            padding: 12px !important;
                            margin-bottom: 10px !important;
                        }

                        .form-itens-table tbody tr[style*="FEF3C7"] {
                            background: #FEF3C7 !important;
                        }

                        .form-itens-table tbody td {
                            display: block !important;
                            padding: 4px 0 !important;
                            text-align: left !important;
                            border: none !important;
                        }

                        .form-itens-table tbody td input,
                        .form-itens-table tbody td select {
                            width: 100% !important;
                        }

                        .form-itens-table tfoot tr {
                            display: flex !important;
                            justify-content: space-between !important;
                            align-items: center !important;
                            padding: 8px 0 !important;
                        }

                        .form-itens-table tfoot td {
                            padding: 4px 0 !important;
                        }

                        .form-itens-table tfoot td[colspan] {
                            display: none !important;
                        }

                        .form-itens-table tfoot td:last-child {
                            text-align: right !important;
                            flex: 1 !important;
                        }

                        .form-itens-table tfoot td input {
                            width: 100% !important;
                        }

                        /* Anexos */
                        .form-anexo-add-grid {
                            grid-template-columns: 1fr !important;
                            gap: 10px !important;
                        }

                        .form-anexos-table thead {
                            display: none !important;
                        }

                        .form-anexos-table tbody tr {
                            display: block !important;
                            background: #F9FAFB !important;
                            border: 1px solid #E5E7EB !important;
                            border-radius: 8px !important;
                            padding: 12px !important;
                            margin-bottom: 10px !important;
                        }

                        .form-anexos-table tbody td {
                            display: block !important;
                            padding: 4px 0 !important;
                            text-align: left !important;
                            word-break: break-all !important;
                        }

                        .form-anexos-table tbody td:last-child {
                            margin-top: 8px !important;
                        }

                        /* Botões de ação */
                        .form-action-buttons {
                            flex-wrap: wrap !important;
                            justify-content: stretch !important;
                        }

                        .form-action-buttons button {
                            flex: 1 1 auto !important;
                            min-width: 45% !important;
                            text-align: center !important;
                            padding: 12px 10px !important;
                            font-size: 13px !important;
                        }
                    }
                `}
            </style>
        </Template>
    );
};

export default PedidoCompraForm;
